// colorExtract.js
// k-means color clustering + color isolation for the artist reference image.

/**
 * Extract dominant colors from an image via k-means clustering.
 * Returns array of { r, g, b, hex, pct } sorted by frequency.
 */
export function extractDominantColors(imageUrl, k = 6) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const maxDim = 200;
      const scale  = Math.min(1, maxDim / Math.max(img.width, img.height));
      canvas.width  = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

      const pixels = [];
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 128) continue;
        pixels.push([data[i], data[i + 1], data[i + 2]]);
      }
      if (pixels.length < k) { resolve([]); return; }

      // Initialize centroids spread across the pixel array
      const step = Math.floor(pixels.length / k);
      let centroids = Array.from({ length: k }, (_, i) => [...pixels[i * step]]);

      for (let iter = 0; iter < 18; iter++) {
        const sums   = Array.from({ length: k }, () => [0, 0, 0]);
        const counts = Array(k).fill(0);
        for (const p of pixels) {
          let minD = Infinity, minC = 0;
          for (let c = 0; c < k; c++) {
            const d = (p[0] - centroids[c][0]) ** 2 + (p[1] - centroids[c][1]) ** 2 + (p[2] - centroids[c][2]) ** 2;
            if (d < minD) { minD = d; minC = c; }
          }
          sums[minC][0] += p[0]; sums[minC][1] += p[1]; sums[minC][2] += p[2];
          counts[minC]++;
        }
        for (let c = 0; c < k; c++) {
          if (counts[c] === 0) continue;
          centroids[c] = [Math.round(sums[c][0] / counts[c]), Math.round(sums[c][1] / counts[c]), Math.round(sums[c][2] / counts[c])];
        }
      }

      // Final assignment to get cluster sizes
      const finalCounts = Array(k).fill(0);
      for (const p of pixels) {
        let minD = Infinity, minC = 0;
        for (let c = 0; c < k; c++) {
          const d = (p[0] - centroids[c][0]) ** 2 + (p[1] - centroids[c][1]) ** 2 + (p[2] - centroids[c][2]) ** 2;
          if (d < minD) { minD = d; minC = c; }
        }
        finalCounts[minC]++;
      }

      const hex = (n) => n.toString(16).padStart(2, "0");
      const results = centroids.map((c, i) => ({
        r: c[0], g: c[1], b: c[2],
        hex: `#${hex(c[0])}${hex(c[1])}${hex(c[2])}`,
        pct: finalCounts[i] / pixels.length,
      }))
        .filter(c => c.pct > 0.01)
        .sort((a, b) => b.pct - a.pct);

      resolve(results);
    };
    img.onerror = () => resolve([]);
    img.src = imageUrl;
  });
}

/**
 * Generate a color-isolated version of the image.
 * Pixels matching targetColor (within threshold) stay opaque; others dim to ~12% alpha.
 * Returns a data URL. Downsamples to 600px max for performance.
 */
export function generateColorIsolation(imageUrl, targetColor, threshold = 55) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const maxDim = 600;
      const scale  = Math.min(1, maxDim / Math.max(img.width, img.height));
      canvas.width  = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const d = imageData.data;

      const tr = targetColor.r, tg = targetColor.g, tb = targetColor.b;
      const t2 = threshold * threshold;

      for (let i = 0; i < d.length; i += 4) {
        const dr = d[i] - tr, dg = d[i + 1] - tg, db = d[i + 2] - tb;
        if (dr * dr + dg * dg + db * db > t2) {
          d[i + 3] = Math.round(d[i + 3] * 0.12);
        }
      }

      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = imageUrl;
  });
}
